import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  useGetAccountCaseloadQuery,
  useGetCaseloadQuery,
  useAddCaseMutation,
  useRemoveCaseMutation,
  useMakeCasePrimaryMutation,
} from 'store/api/careManagement';
import { useGetAccountQuery } from 'store/api/account';
import { getAccount } from 'store/slices/iamSlice';
import { newToast } from 'store/slices/uiSlice';
import { AppDispatch } from 'store';

export const useAdminCaseload = (accountId?: string) => {
  const currentAccount = useSelector(getAccount);
  const dispatch = useDispatch<AppDispatch>();

  const targetAccountId = accountId || currentAccount?.id;
  const isOwnCaseload = targetAccountId === currentAccount?.id;

  const { data: targetAccountData } = useGetAccountQuery(
    { accountId: targetAccountId ?? '' },
    { skip: !targetAccountId || isOwnCaseload },
  );

  const targetAccount = isOwnCaseload ? currentAccount : targetAccountData;

  const ownerName = useMemo(() => {
    if (isOwnCaseload) return 'your';
    if (!targetAccount) return '';
    const name =
      `${targetAccount.firstName ?? ''} ${targetAccount.lastName ?? ''}`.trim();
    return name ? `${name}'s` : '';
  }, [isOwnCaseload, targetAccount]);

  const { data: caseloadMeta } = useGetAccountCaseloadQuery(
    { accountId: targetAccountId ?? '' },
    { skip: !targetAccountId },
  );

  const [addCaseMutation] = useAddCaseMutation();
  const [removeCaseMutation] = useRemoveCaseMutation();
  const [makePrimaryMutation] = useMakeCasePrimaryMutation();

  const caseloadId = caseloadMeta?.id;

  const { data: caseloadWithCases } = useGetCaseloadQuery(
    { caseloadId: caseloadId ?? '' },
    { skip: !caseloadId },
  );

  const casesByCareCircle = useMemo(() => {
    const map = new Map<string, { caseId: string; isPrimary: boolean }>();
    for (const c of caseloadWithCases?.cases ?? []) {
      map.set(c.careCircleId, { caseId: c.id, isPrimary: c.isPrimary });
    }
    return map;
  }, [caseloadWithCases]);

  const addToCaseload = useCallback(
    async (careCircleId: string) => {
      if (!caseloadId) return false;
      const res = await addCaseMutation({
        caseloadId,
        body: { careCircleId },
      });
      if ('error' in res) {
        dispatch(
          newToast({
            title: 'Error',
            description: `Failed to add to ${ownerName} caseload.`,
            variant: 'error',
          }),
        );
        return false;
      }
      dispatch(
        newToast({
          title: 'Added',
          description: `Care circle added to ${ownerName} caseload.`,
        }),
      );
      return true;
    },
    [caseloadId, addCaseMutation, dispatch, ownerName],
  );

  const removeFromCaseload = useCallback(
    async (caseId: string) => {
      if (!caseloadId) return false;
      const res = await removeCaseMutation({
        caseloadId,
        caseId,
      });
      if ('error' in res) {
        dispatch(
          newToast({
            title: 'Error',
            description: `Failed to remove from ${ownerName} caseload.`,
            variant: 'error',
          }),
        );
        return false;
      }
      dispatch(
        newToast({
          title: 'Removed',
          description: `Care circle removed from ${ownerName} caseload.`,
        }),
      );
      return true;
    },
    [caseloadId, removeCaseMutation, dispatch, ownerName],
  );

  const removeFromCaseloadByCareCircle = useCallback(
    async (careCircleId: string) => {
      const entry = casesByCareCircle.get(careCircleId);
      if (!entry) return false;
      return removeFromCaseload(entry.caseId);
    },
    [casesByCareCircle, removeFromCaseload],
  );

  const makePrimary = useCallback(
    async (careCircleId: string) => {
      const entry = casesByCareCircle.get(careCircleId);
      if (!entry) return false;
      const res = await makePrimaryMutation({
        careCircleId,
        caseId: entry.caseId,
      });
      if ('error' in res) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to set as primary case manager.',
            variant: 'error',
          }),
        );
        return false;
      }
      dispatch(
        newToast({
          title: 'Updated',
          description: 'You are now the primary case manager.',
        }),
      );
      return true;
    },
    [casesByCareCircle, makePrimaryMutation, dispatch],
  );

  const getCaseStatus = useCallback(
    (careCircleId: string) => {
      return casesByCareCircle.get(careCircleId) ?? null;
    },
    [casesByCareCircle],
  );

  return {
    caseloadId,
    hasCaseload: !!caseloadId,
    ownerName,
    targetAccount,
    isOwnCaseload,
    addToCaseload,
    removeFromCaseload,
    removeFromCaseloadByCareCircle,
    makePrimary,
    getCaseStatus,
  };
};
